import Card from "@rio-cloud/rio-uikit/Card";
import EconomyRankingHeader from "@components/shared/EconomyRankingHeader";
import { TopVehiclesMaintenanceSkeletonRows } from "@components/Skeletons/TopVehiclesMaintenanceTableSkeleton";
import { useT } from "@hooks";
import type { MaintenanceTopVehiclesOrder, MaintenanceVehicleCost } from "@services/maintenance";

interface TopVehiclesMaintenanceTableProps {
  items: MaintenanceVehicleCost[];
  order: MaintenanceTopVehiclesOrder;
  onOrderChange: (order: MaintenanceTopVehiclesOrder) => void;
  isLoading?: boolean;
}

const HEADER_CELL_STYLE = { fontWeight: 500, fontSize: 13 } as const;
const HEADER_CELL_NOWRAP_STYLE = { fontWeight: 500, fontSize: 13, whiteSpace: "nowrap" } as const;
const BODY_CELL_STYLE = { fontSize: 13 } as const;
const BODY_CELL_NOWRAP_STYLE = { fontSize: 13, whiteSpace: "nowrap" } as const;
const EMPTY_VALUE = "-";

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatCostPerKm = (value: number | null) => (value ? formatCurrency(value) : EMPTY_VALUE);

export default function TopVehiclesMaintenanceTable({
  items,
  order,
  onOrderChange,
  isLoading = false,
}: Readonly<TopVehiclesMaintenanceTableProps>) {
  const t = useT();

  const renderRows = () => {
    if (isLoading) return <TopVehiclesMaintenanceSkeletonRows />;

    if (items.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="text-center text-color-gray padding-20">
            {t("common.label.noData")}
          </td>
        </tr>
      );
    }

    return items.map((vehicle) => (
      <tr key={vehicle.vehicle}>
        <td style={BODY_CELL_NOWRAP_STYLE}>{vehicle.vehicle}</td>
        <td style={BODY_CELL_STYLE}>{formatCurrency(vehicle.amount)}</td>
        <td style={BODY_CELL_STYLE}>{formatCostPerKm(vehicle.costPerKm)}</td>
      </tr>
    ));
  };

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm display-flex flex-column height-100pct">
      <EconomyRankingHeader
        title={t("maintenance.chart.topVehicles")}
        mostEconomicalLabel={t("maintenance.chart.topVehicles.mostEconomical")}
        leastEconomicalLabel={t("maintenance.chart.topVehicles.leastEconomical")}
        showMostEconomical={order === "MOST_ECONOMIC"}
        onShowMostEconomicalChange={(showMostEconomical) =>
          onOrderChange(showMostEconomical ? "MOST_ECONOMIC" : "LEAST_ECONOMIC")
        }
      />

      <div className="table-responsive">
        <table className="table table-hover" style={{ marginBottom: 0 }}>
          <thead>
            <tr className="bg-lightest">
              <th className="text-color-gray" style={HEADER_CELL_NOWRAP_STYLE}>
                {t("maintenance.chart.topVehicles.vehicle")}
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                {t("maintenance.chart.topVehicles.amount")}
              </th>
              <th className="text-color-gray" style={HEADER_CELL_STYLE}>
                {t("maintenance.chart.topVehicles.costPerKm")}
              </th>
            </tr>
          </thead>
          <tbody data-testid="corpo-veiculos">{renderRows()}</tbody>
        </table>
      </div>
    </Card>
  );
}
